import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'antd';
import { debounce } from 'underscore';
import { useTranslation } from 'react-i18next';

import { HorizontalLayout, RadiobuttonGroupSection, RadiobuttonWrapper } from './styles';
import { CheckboxGroupSettings } from './CheckboxGroupSettings';
import { PageElementsContext } from '../../../../context/pageElementsContext';
import { ELEMENTS_SETTINGS_TOOLBAR } from '../../../../artboard/Toolbar/translationValues';
import { ColorButton } from '../../../../artboard/Toolbar/styles';
import ColorPicker from '../../../../artboard/ColorPicker';
import { deepCopy } from '../../../../utils/javascript';
import {
  GROUP_NAME_KEY,
  OPTIONS_KEY,
  TEXT_DEBOUNCE_TIME_OUT,
} from '../../../../utils/complexDocumentConstants';
import { CONTEXT_KEYS, useArtboardContext } from '../../../../context/complexArtboardContext';
import { FillableElementWrapper } from '../../styles';

const allStyleKeys = ['borderColor', 'backgroundColor', 'color'];
const [borderColorKey, ...styleKeys] = allStyleKeys;

const RadioButton = ({ editable, element, checked }) => {
  return (
    <label
      className={`ant-radio-wrapper ${editable ? '' : 'ant-radio-wrapper-disabled'} ${
        checked ? 'ant-radio-wrapper-checked' : ''
      }`}
    >
      <span
        className={`ant-radio ${editable ? '' : 'ant-radio-disabled'} ${
          checked ? 'ant-radio-checked' : ''
        }`}
      >
        <input
          type="radio"
          className="ant-radio-input"
          name={element[OPTIONS_KEY][GROUP_NAME_KEY]}
          checked={true}
        />
        <span className="ant-radio-inner"></span>
      </span>
    </label>
  );
};

const NAMESPACES = ['ELEMENTS_SETTINGS_TOOLBAR', 'FILLABLE_ELEMENTS_MAIN_CONTENT'];

export const RadiobuttonGroup = ({ element, editable, selected, updateElementRect }) => {
  const { contents, style } = element;

  const [picker, setPicker] = useState(false);
  const { updateComplexElementDetails } = useContext(PageElementsContext);
  const { t } = useTranslation(NAMESPACES);

  const [, updateArtboard] = useArtboardContext(() => null);
  useEffect(() => {
    // close the sidebar when the element is removed from the block.
    return () => {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    };
  }, []);

  const debouncedUpdateComplexElementDetails = useRef(
    debounce(updateComplexElementDetails, TEXT_DEBOUNCE_TIME_OUT),
  ).current;

  const updateElementDetails = useCallback(
    (value, key) => {
      const details = deepCopy(contents);
      if (styleKeys.includes(key)) {
        details.style = { ...details.style, [key]: value };
      } else if (key === borderColorKey) {
        details.style = { ...details.style, border: { [key]: value } };
      } else {
        details.formOptions = { ...details.formOptions, [key]: value };
      }
      updateComplexElementDetails(element.uuid, { contents: details }, 'form-options');
    },
    [updateComplexElementDetails, element, contents],
  );

  const updateElementStyles = useCallback(
    values => {
      const details = deepCopy(contents);
      details.style = { ...details.style, ...values };
      updateComplexElementDetails(element.uuid, { contents: details }, 'form-options');
    },
    [updateComplexElementDetails, element, contents],
  );

  const updateTextStyle = useCallback(
    values => {
      const details = { style: { ...values } };
      updateComplexElementDetails(element.uuid, details, 'form-options');
    },
    [element.uuid, updateComplexElementDetails],
  );

  const onGroupNameChange = useCallback(
    value => {
      const details = deepCopy(contents);
      details.formOptions = { ...details.formOptions, groupName: value };
      debouncedUpdateComplexElementDetails(element.uuid, { contents: details }, 'form-options');
    },
    [debouncedUpdateComplexElementDetails, contents, element.uuid],
  );

  const showBackgroundColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'backgroundColor') {
        return false;
      }
      return { type: 'backgroundColor', color: contents.style.backgroundColor };
    });
  }, [contents]);

  const showBorderColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'borderColor') {
        return false;
      }
      return { type: 'borderColor', color: contents.style.border?.borderColor };
    });
  }, [contents]);

  const showElementColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'color') {
        return false;
      }
      return { type: 'color', color: contents.style.border?.borderColor };
    });
  }, [contents]);

  const onColorPick = useCallback(
    color => {
      if (picker.type === 'backgroundColor') {
        updateElementDetails(color.hex, 'backgroundColor');
        setPicker({ type: 'backgroundColor', color: color.hex });
      } else if (picker.type === 'borderColor') {
        updateElementDetails(color.hex, 'borderColor');
        setPicker({ type: 'borderColor', color: color.hex });
      } else if (picker.type === 'color') {
        updateElementDetails(color.hex, 'color');
        setPicker({ type: 'color', color: color.hex });
      }
    },
    [picker, updateElementDetails],
  );

  const textStyle = {
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    color: style.color,
    backgroundColor: style.backgroundColor,
    textDecoration: style.fontWeight?.includes('underline') ? 'underline' : '',
    fontWeight: style.fontWeight?.includes('bold') ? 'bold' : '',
    fontStyle: style.fontWeight?.includes('italics') ? 'italic' : '',
  };

  return (
    <RadiobuttonGroupSection
      borderColor={contents.style?.border?.borderColor}
      backgroundColor={contents.style?.backgroundColor}
      color={contents.style?.color}
      direction={contents?.formOptions?.direction}
      style={{ pointerEvents: !editable && 'none' }}
      className={`complex-documents`}
    >
      {contents?.formOptions?.direction === 'vertical' ? (
        <>
          <RadiobuttonWrapper width={contents.style.width} height={contents.style.height}>
            <RadioButton checked={true} editable={editable} element={contents} />
            <span style={textStyle}>Radio button</span>
          </RadiobuttonWrapper>
          <RadiobuttonWrapper width={contents.style.width} height={contents.style.height}>
            <RadioButton checked={true} editable={editable} element={contents} />
            <span style={textStyle}>Radio button</span>
          </RadiobuttonWrapper>
        </>
      ) : (
        <HorizontalLayout width={contents.style.width} height={contents.style.height}>
          <RadioButton checked={true} editable={editable} element={contents} />
          <span style={textStyle}>Radio button</span>
          <RadioButton checked={true} editable={editable} element={contents} />
          <span style={textStyle}>Radio button</span>
        </HorizontalLayout>
      )}
      {editable &&
        selected &&
        ReactDOM.createPortal(
          <CheckboxGroupSettings
            element={element}
            updateElementDetails={updateElementDetails}
            onGroupNameChange={onGroupNameChange}
            updateTextStyle={updateTextStyle}
            updateElementStyles={updateElementStyles}
            selected={selected}
            isRadio={true}
            updateElementRect={updateElementRect}
          />,
          document.getElementById('sidebar-absolute-container'),
        )}
      {editable &&
        selected &&
        ReactDOM.createPortal(
          <FillableElementWrapper>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{ backgroundColor: contents.style.backgroundColor, position: 'relative' }}
                onClick={showBackgroundColorPicker}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{
                  backgroundColor: contents.style.border?.borderColor,
                  position: 'relative',
                }}
                onClick={showBorderColorPicker}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_COLOR_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_COLOR_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{ backgroundColor: contents.style.color, position: 'relative' }}
                onClick={showElementColorPicker}
              />
            </Tooltip>
            {picker && (
              <div style={{ position: 'absolute', top: '100%' }}>
                <ColorPicker
                  onChange={onColorPick}
                  onBlur={() => setPicker(false)}
                  color={picker.color}
                  qlEditor={true}
                />
              </div>
            )}
          </FillableElementWrapper>,
          document.getElementById('document-editor-toolbar'),
        )}
    </RadiobuttonGroupSection>
  );
};
